import React from 'react';
import pt from 'prop-types';
import cx from 'classnames';

import { confirmRouteLeave, confirmPageLeave } from 'utils/container';
import { shouldFetchPageRecord } from 'components/storefront/editor/utils/page-data';

import Button from 'components/button';
import SkeletonWrapper from 'components/skeleton';

import EasyblocksBackendProvider from './backendContext';

import Easyblocks from './easyblocks';
import StorefrontEditorHeader from './header';
import StorefrontEditorSubHeader from './sub-header';
import StorefrontEditorSidebar from './sidebar';
import StorefrontEditorOverlay from './overlay';
import StorefrontEditorPageAdd from './modals/page-add';
import StorefrontCodeEditor from './code/editor';

import './editor.scss';

export default class StorefrontEditor extends React.PureComponent {
  static contextTypes = {
    openModal: pt.func.isRequired,
  };

  static propTypes = {
    pages: pt.shape({
      templates: pt.array,
      pages: pt.array,
    }),
    page: pt.object,
    sectionGroups: pt.array,
    focusedField: pt.string,
    location: pt.object,
    editorConfig: pt.object,
    loading: pt.bool,
    viewport: pt.string,
    error: pt.string,
    onAddPage: pt.func,
    onDuplicatePage: pt.func,
    onDeletePage: pt.func,
    onClickErrorTryAgain: pt.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      edited: false,
      addingPage: false,
      easyblocksConfig: null,
      onLoadEasyblocksConfig: this.onLoadEasyblocksConfig.bind(this),
      onChange: this.onChange.bind(this),
    };
  }

  componentDidMount() {
    confirmRouteLeave(this);
  }

  componentDidUpdate(_prevProps, prevState) {
    confirmPageLeave(this, prevState);
  }

  componentWillUnmount() {
    confirmPageLeave(this);
  }

  onChange = (edited) => {
    this.setState({ edited });
  };

  onLoadEasyblocksConfig = (easyblocksConfig) => {
    this.setState({
      easyblocksConfig,
    });
  };

  onClickAddPage = () => {
    this.setState({ addingPage: !this.state.addingPage });
  };

  renderError() {
    const { error, onClickErrorTryAgain } = this.props;

    return (
      <div className="storefront-editor-error--container">
        <div className="storefront-editor-error--box">
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <Button type="default" size="sm" onClick={onClickErrorTryAgain}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  render() {
    const {
      pages,
      page,
      loading,
      location,
      editorConfig,
      viewport,
      onAddPage,
      onDuplicatePage,
      onDeletePage,
      error,
      inCodeEditor,
    } = this.props;
    const { addingPage, easyblocksConfig } = this.state;

    const shouldRenderSubHeader =
      !loading && viewport !== 'expand' && shouldFetchPageRecord(page);

    return (
      <EasyblocksBackendProvider {...this.state} {...this.props}>
        <SkeletonWrapper>
          <div className="storefront-editor">
            <StorefrontEditorHeader
              {...this.props}
              inCodeEditor={inCodeEditor}
            />
            <div
              className={cx('storefront-editor--container', {
                expanded: viewport === 'expand',
              })}
            >
              {error ? (
                this.renderError()
              ) : (
                <>
                  {inCodeEditor ? (
                    <StorefrontCodeEditor
                      {...this.props}
                      onEdited={this.onChange}
                    />
                  ) : (
                    <>
                      <StorefrontEditorSidebar
                        {...this.props}
                        easyblocksConfig={easyblocksConfig}
                        onClickAddPage={this.onClickAddPage}
                        inCodeEditor={inCodeEditor}
                      />
                      <div className="storefront-editor--editor">
                        {shouldRenderSubHeader && (
                          <StorefrontEditorSubHeader {...this.props} />
                        )}
                        <Easyblocks
                          {...this.props}
                          {...this.state}
                          subheader={shouldRenderSubHeader}
                        />
                      </div>
                      <StorefrontEditorOverlay
                        pageId={page.id}
                        editorConfig={editorConfig}
                        easyblocksConfig={easyblocksConfig}
                        location={location}
                        onDuplicatePage={onDuplicatePage}
                        onDeletePage={onDeletePage}
                      />
                    </>
                  )}
                </>
              )}
            </div>
            {addingPage && (
              <StorefrontEditorPageAdd
                pages={pages}
                onSubmit={onAddPage}
                onClose={this.onClickAddPage}
              />
            )}
          </div>
        </SkeletonWrapper>
      </EasyblocksBackendProvider>
    );
  }
}
